import { api } from './client';
import type {
  User, Organization, Deal, DealDetail, DealRoom,
  Template, Task, Document, DocumentView, Activity, Comment,
  Notification, DashboardAnalytics,
} from '../types';

const API_BASE = import.meta.env.VITE_API_URL || '';

// ===== Auth =====
export const auth = {
  sendMagicLink: (email: string) => api.post<{ message: string; token?: string; _dev_link?: string }>('/api/auth/magic-link', { email }),
  verify: (token: string) => api.post<{ session_token: string; user: User }>('/api/auth/verify', { token }),
  logout: () => api.post('/api/auth/logout'),
  me: () => api.get<User>('/api/auth/me'),
};

// ===== Organization =====
export const org = {
  get: () => api.get<Organization>('/api/organization'),
  update: (data: Partial<Organization>) => api.put<Organization>('/api/organization', data),
};

// ===== Team =====
export const team = {
  list: () => api.get<User[]>('/api/team'),
  invite: (data: { email: string; name?: string; role?: string }) => api.post<User>('/api/team/invite', data),
  update: (userId: number, data: { role?: string; name?: string }) => api.put('/api/team/' + userId, data),
  remove: (userId: number) => api.del('/api/team/' + userId),
};

// ===== Deals =====
export const deals = {
  list: (stage?: string) => api.get<Deal[]>('/api/deals' + (stage ? `?stage=${stage}` : '')),
  get: (id: number) => api.get<DealDetail>('/api/deals/' + id),
  create: (data: Partial<Deal>) => api.post<Deal>('/api/deals', data),
  update: (id: number, data: Partial<Deal>) => api.put<Deal>('/api/deals/' + id, data),
  remove: (id: number) => api.del('/api/deals/' + id),
};

// ===== Deal Members =====
export const dealMembers = {
  add: (dealId: number, data: { email: string; name?: string; role?: string; side?: string }) =>
    api.post('/api/deals/' + dealId + '/members', data),
  remove: (dealId: number, memberId: number) =>
    api.del('/api/deals/' + dealId + '/members/' + memberId),
};

// ===== Deal Room =====
export const dealRooms = {
  get: (dealId: number) => api.get<DealRoom>('/api/deals/' + dealId + '/room'),
  update: (dealId: number, data: Partial<DealRoom>) => api.put<DealRoom>('/api/deals/' + dealId + '/room', data),
  getPublic: (token: string) => api.get<{ room: DealRoom; documents: Document[]; tasks: Task[]; members: { name: string; role: string; side: string }[] }>('/api/room/' + token),
};

// ===== Templates =====
export const templates = {
  list: () => api.get<Template[]>('/api/templates'),
  create: (data: Partial<Template>) => api.post<Template>('/api/templates', data),
  update: (id: number, data: Partial<Template>) => api.put<Template>('/api/templates/' + id, data),
  remove: (id: number) => api.del('/api/templates/' + id),
};

// ===== Tasks =====
export const tasks = {
  list: (dealId: number, status?: string) =>
    api.get<Task[]>('/api/deals/' + dealId + '/tasks' + (status ? `?status=${status}` : '')),
  create: (dealId: number, data: Partial<Task>) => api.post<Task>('/api/deals/' + dealId + '/tasks', data),
  update: (dealId: number, taskId: number, data: Partial<Task>) =>
    api.put<Task>('/api/deals/' + dealId + '/tasks/' + taskId, data),
  remove: (dealId: number, taskId: number) => api.del('/api/deals/' + dealId + '/tasks/' + taskId),
};

// ===== Documents (with file upload) =====
export const documents = {
  list: (dealId: number) => api.get<Document[]>('/api/deals/' + dealId + '/documents'),
  upload: async (dealId: number, file: File): Promise<Document> => {
    const token = localStorage.getItem('dealroom-token');
    const res = await fetch(`${API_BASE}/api/deals/${dealId}/documents/upload`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'X-File-Name': file.name,
        'X-File-Type': file.name.split('.').pop()?.toUpperCase() || 'OTHER',
        'X-File-Size': String(file.size),
      },
      body: file,
    });
    if (!res.ok) {
      const data = await res.json() as { error?: string };
      throw new Error(data.error || '업로드 실패');
    }
    return res.json() as Promise<Document>;
  },
  update: (dealId: number, docId: number, data: Partial<Document>) =>
    api.put<Document>('/api/deals/' + dealId + '/documents/' + docId, data),
  remove: (dealId: number, docId: number) => api.del('/api/deals/' + dealId + '/documents/' + docId),
  views: (dealId: number) => api.get<DocumentView[]>('/api/deals/' + dealId + '/analytics/views'),
  downloadUrl: (docId: number) => `${API_BASE}/api/documents/${docId}/download`,
  recordView: (docId: number, data: Partial<DocumentView>) =>
    api.post('/api/documents/' + docId + '/views', data),
};

// ===== Activity =====
export const activity = {
  list: (dealId: number) => api.get<Activity[]>('/api/deals/' + dealId + '/activity'),
};

// ===== Comments =====
export const comments = {
  list: (dealId: number, params?: { task_id?: number; document_id?: number }) => {
    const qs = new URLSearchParams();
    if (params?.task_id) qs.set('task_id', String(params.task_id));
    if (params?.document_id) qs.set('document_id', String(params.document_id));
    const q = qs.toString();
    return api.get<Comment[]>('/api/deals/' + dealId + '/comments' + (q ? '?' + q : ''));
  },
  create: (dealId: number, data: { body: string; task_id?: number; document_id?: number }) =>
    api.post<Comment>('/api/deals/' + dealId + '/comments', data),
};

// ===== Notifications =====
export const notifications = {
  list: () => api.get<{ notifications: Notification[]; unread_count: number }>('/api/notifications'),
  readAll: () => api.put('/api/notifications/read-all'),
  read: (id: number) => api.put('/api/notifications/' + id),
};

// ===== Analytics =====
export const analytics = {
  dashboard: () => api.get<DashboardAnalytics>('/api/analytics/dashboard'),
};

// ===== Billing =====
export const billing = {
  getPlan: () => api.get<{
    plan: string; plan_seats: number; plan_storage_gb: number;
    usage: { deals: number; members: number; storage_bytes: number };
    limits: { deals: number; members: number; storage_gb: number; templates: number };
  }>('/api/billing/plan'),
  checkout: (plan: string) => api.post<{ checkout_url: string }>('/api/billing/checkout', { plan }),
  portal: () => api.post<{ portal_url: string }>('/api/billing/portal'),
};
